import styled from 'styled-components';
import { DesignSystem, getColor, getSpacing, getShadow } from '../styles/designSystem';

interface ButtonProps {
  variant?: 'primary' | 'secondary' | 'ghost';
  fullWidth?: boolean;
  size?: 'sm' | 'md' | 'lg';
}

export const Button = styled.button<ButtonProps>`
  padding: ${props => {
    switch (props.size) {
      case 'sm': return `${getSpacing('sm')} ${getSpacing('md')}`;
      case 'lg': return `${getSpacing('lg')} ${getSpacing('xl')}`;
      default: return `${getSpacing('md')} ${getSpacing('lg')}`;
    }
  }};
  border: none;
  border-radius: ${DesignSystem.borderRadius.lg};
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all ${DesignSystem.motion.duration.fast} ease;
  background: ${props => {
    switch (props.variant) {
      case 'secondary': return getColor('accent.600');
      case 'ghost': return 'transparent';
      default: return getColor('primary.600');
    }
  }};
  color: ${props => {
    switch (props.variant) {
      case 'ghost': return getColor('primary.600');
      default: return 'white';
    }
  }};
  border: ${props => props.variant === 'ghost' ? `2px solid ${getColor('primary.200')}` : 'none'};
  width: ${props => props.fullWidth ? '100%' : 'auto'};
  
  &:hover {
    background: ${props => {
      switch (props.variant) {
        case 'secondary': return getColor('accent.700');
        case 'ghost': return getColor('primary.50');
        default: return getColor('primary.700');
      }
    }};
    transform: translateY(-2px);
  }
`;

export const Card = styled.div`
  background: white;
  border-radius: ${DesignSystem.borderRadius.xl};
  padding: ${getSpacing('xl')};
  box-shadow: ${getShadow('soft')};
`;

interface ContainerProps {
  maxWidth?: 'sm' | 'md' | 'lg' | 'xl' | '2xl';
}

const maxWidthValues = {
  sm: '640px',
  md: '768px',
  lg: '1024px',
  xl: '1280px',
  '2xl': '1536px'
};

export const Container = styled.div<ContainerProps>`
  max-width: ${props => props.maxWidth ? maxWidthValues[props.maxWidth] : '1200px'};
  margin: 0 auto;
  padding: 0 ${getSpacing('lg')};
`;

interface GridProps {
  cols: number;
  gap?: 'sm' | 'md' | 'lg' | 'xl';
  responsiveCols?: {
    sm?: number;
    md?: number;
    lg?: number;
  };
}

export const Grid = styled.div<GridProps>`
  display: grid;
  grid-template-columns: repeat(${props => props.cols}, 1fr);
  gap: ${props => getSpacing(props.gap || 'md')};
  
  ${props => props.responsiveCols?.sm && `
    @media (min-width: 640px) {
      grid-template-columns: repeat(${props.responsiveCols.sm}, 1fr);
    }
  `}
  
  ${props => props.responsiveCols?.md && `
    @media (min-width: 768px) {
      grid-template-columns: repeat(${props.responsiveCols.md}, 1fr);
    }
  `}
  
  ${props => props.responsiveCols?.lg && `
    @media (min-width: 1024px) {
      grid-template-columns: repeat(${props.responsiveCols.lg}, 1fr);
    }
  `}
`;

export const Heading1 = styled.h1`
  font-size: ${DesignSystem.typography.scale.h1.size};
  font-weight: ${DesignSystem.typography.scale.h1.weight};
  line-height: ${DesignSystem.typography.scale.h1.lineHeight};
  letter-spacing: ${DesignSystem.typography.scale.h1.letterSpacing};
  margin: 0;
`;

export const Heading2 = styled.h2`
  font-size: ${DesignSystem.typography.scale.h2.size};
  font-weight: ${DesignSystem.typography.scale.h2.weight};
  line-height: ${DesignSystem.typography.scale.h2.lineHeight};
  letter-spacing: ${DesignSystem.typography.scale.h2.letterSpacing};
  margin: 0;
`;

export const Heading3 = styled.h3`
  font-size: ${DesignSystem.typography.scale.h3.size};
  font-weight: ${DesignSystem.typography.scale.h3.weight};
  line-height: ${DesignSystem.typography.scale.h3.lineHeight};
  letter-spacing: ${DesignSystem.typography.scale.h3.letterSpacing};
  margin: 0;
`;

// Premium display headings for dramatic impact
export const DisplayLarge = styled.h1`
  font-size: ${DesignSystem.typography.scale.displayLarge.size};
  font-weight: ${DesignSystem.typography.scale.displayLarge.weight};
  line-height: ${DesignSystem.typography.scale.displayLarge.lineHeight};
  letter-spacing: ${DesignSystem.typography.scale.displayLarge.letterSpacing};
  margin: 0;
`;

export const DisplayMedium = styled.h2`
  font-size: ${DesignSystem.typography.scale.displayMedium.size};
  font-weight: ${DesignSystem.typography.scale.displayMedium.weight};
  line-height: ${DesignSystem.typography.scale.displayMedium.lineHeight};
  letter-spacing: ${DesignSystem.typography.scale.displayMedium.letterSpacing};
  margin: 0;
`;

export const BodyText = styled.p`
  font-size: 1rem;
  line-height: 1.6;
  margin: 0;
`;

export const BodyLarge = styled.p`
  font-size: 1.125rem;
  line-height: 1.6;
  margin: 0;
`;

export const Caption = styled.span`
  font-size: 1.1rem;
  line-height: 1.5;
  color: ${getColor('primary.900')};
`;

interface FlexProps {
  direction?: 'row' | 'column';
  align?: 'start' | 'center' | 'end' | 'stretch';
  justify?: 'start' | 'center' | 'end' | 'between' | 'around';
  gap?: 'sm' | 'md' | 'lg' | 'xl';
  wrap?: boolean;
}

export const Flex = styled.div<FlexProps>`
  display: flex;
  flex-direction: ${props => props.direction || 'row'};
  align-items: ${props => {
    switch (props.align) {
      case 'start': return 'flex-start';
      case 'center': return 'center';
      case 'end': return 'flex-end';
      case 'stretch': return 'stretch';
      default: return 'flex-start';
    }
  }};
  justify-content: ${props => {
    switch (props.justify) {
      case 'start': return 'flex-start';
      case 'center': return 'center';
      case 'end': return 'flex-end';
      case 'between': return 'space-between';
      case 'around': return 'space-around';
      default: return 'flex-start';
    }
  }};
  gap: ${props => props.gap ? getSpacing(props.gap) : '0'};
  ${props => props.wrap && 'flex-wrap: wrap;'}
`;

export const Spacer = styled.div<{ size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' }>`
  height: ${props => getSpacing(props.size || 'md')};
`;

interface BadgeProps {
  variant?: 'primary' | 'secondary' | 'luxury';
  size?: 'sm' | 'md' | 'lg';
}

export const Badge = styled.span<BadgeProps>`
  display: inline-block;
  padding: ${props => {
    switch (props.size) {
      case 'sm': return `${getSpacing('xs')} ${getSpacing('sm')}`;
      case 'lg': return `${getSpacing('sm')} ${getSpacing('lg')}`;
      default: return `${getSpacing('xs')} ${getSpacing('sm')}`;
    }
  }};
  background: ${props => {
    switch (props.variant) {
      case 'secondary': return getColor('accent.100');
      case 'luxury': return getColor('neutral.gray.100');
      default: return getColor('primary.100');
    }
  }};
  color: ${props => {
    switch (props.variant) {
      case 'secondary': return getColor('accent.700');
      case 'luxury': return getColor('neutral.gray.700');
      default: return getColor('primary.700');
    }
  }};
  border-radius: ${DesignSystem.borderRadius.md};
  font-size: ${props => {
    switch (props.size) {
      case 'sm': return '0.625rem';
      case 'lg': return '0.875rem';
      default: return '0.75rem';
    }
  }};
  font-weight: 600;
`;

export const DestinationChip = styled(Badge)`
  background: ${props => {
    switch (props.variant) {
      case 'primary': return getColor('primary.50');
      case 'secondary': return getColor('accent.50');
      default: return getColor('neutral.gray.50');
    }
  }};
  color: ${props => {
    switch (props.variant) {
      case 'primary': return getColor('primary.600');
      case 'secondary': return getColor('accent.600');
      default: return getColor('neutral.gray.600');
    }
  }};
  border: 1px solid ${props => {
    switch (props.variant) {
      case 'primary': return getColor('primary.200');
      case 'secondary': return getColor('accent.200');
      default: return getColor('neutral.gray.200');
    }
  }};
`;